import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL, buildApiUrl } from '../services/api';
import { PaymentNotice } from '../types/payment';

interface User {
  id: string;
  nome: string;
  email: string;
  document: string;
  tenantId?: string | null;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    active?: boolean;
  } | null;
  registroNumero?: string | null;
  orgaoEmissor?: string | null;
  role: string;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
  trialEndsAt?: string | null;
  subscriptionStatus?: string | null;
  paymentStatus?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  paymentNotice: PaymentNotice | null;
  paymentRequired: boolean;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
  setPaymentNotice: (notice: PaymentNotice | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const derivePaymentRequired = (notice: PaymentNotice | null): boolean => {
  if (!notice) return false;
  if (notice.requiresPayment || notice.paymentRequired) return true;
  const status = typeof notice.status === 'string' ? notice.status.toUpperCase() : '';
  return ['REQUIRES_PAYMENT', 'PAYMENT_REQUIRED', 'PAST_DUE', 'EXPIRED', 'TRIAL_ENDED'].includes(status);
};

const extractPaymentNotice = (payload: any): PaymentNotice | null => {
  const noticeFromData =
    payload?.data?.paymentNotice ??
    payload?.paymentNotice ??
    payload?.data?.subscription?.paymentNotice ??
    payload?.subscription?.paymentNotice;

  if (noticeFromData) {
    return noticeFromData as PaymentNotice;
  }

  const looksLikeNotice =
    payload?.requiresPayment ||
    payload?.paymentRequired ||
    payload?.status ||
    payload?.message;

  return looksLikeNotice ? (payload as PaymentNotice) : null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [paymentNotice, setPaymentNotice] = useState<PaymentNotice | null>(null);

  const isAuthenticated = !!user && !!token;
  const paymentRequired = derivePaymentRequired(paymentNotice);

  // Interceptar requisições para adicionar token
  const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(buildApiUrl(url), {
      ...options,
      headers,
    });
  };

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await fetch(buildApiUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, senha }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || 'Erro ao fazer login');
        return false;
      }

      const { token: newToken, user: userData } = data.data;
      const extractedNotice = extractPaymentNotice(data);

      setToken(newToken);
      setUser(userData);
      setPaymentNotice(extractedNotice);
      localStorage.setItem('auth_token', newToken);

      toast.success('Login realizado com sucesso!');
      if (extractedNotice?.message) {
        toast(extractedNotice.message, { icon: '💳' });
      }
      return true;
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error('Erro de conexão. Tente novamente.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPaymentNotice(null);
    localStorage.removeItem('auth_token');
    toast.success('Logout realizado com sucesso!');
  };

  const checkAuth = async (): Promise<boolean> => {
    if (!token) {
      setIsLoading(false);
      return false;
    }

    try {
      const response = await apiRequest('/auth/verify');

      if (!response.ok) {
        logout();
        return false;
      }

      const data = await response.json();
      setUser(data.data.user);
      setPaymentNotice(extractPaymentNotice(data));
      return true;
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      logout();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const handlePaymentRequired = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const notice = extractPaymentNotice(detail);
      if (notice) {
        setPaymentNotice(notice);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('app:payment-required', handlePaymentRequired as EventListener);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('app:payment-required', handlePaymentRequired as EventListener);
      }
    };
  }, []);

  // Interceptar respostas 401 da API para fazer logout automático
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Só faz logout se for uma resposta 401 de uma rota da API
      const requestUrl =
        typeof args[0] === 'string'
          ? args[0]
          : args[0] instanceof Request
            ? args[0].url
            : '';

      const normalizedUrl = requestUrl.startsWith(window.location.origin)
        ? requestUrl.replace(window.location.origin, '')
        : requestUrl;
      const shouldIgnoreAutoLogout = normalizedUrl.includes('/api/mandates/read-pdf');

      if (
        response.status === 401 &&
        isAuthenticated &&
        !shouldIgnoreAutoLogout &&
        normalizedUrl &&
        (normalizedUrl.startsWith('/api') || normalizedUrl.startsWith(API_BASE_URL))
      ) {
        console.log('Token expirado ou inválido, fazendo logout...');
        logout();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, logout]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    paymentNotice,
    paymentRequired,
    login,
    logout,
    checkAuth,
    setUser,
    setToken,
    setPaymentNotice,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
